import { useCallback, useEffect, useState } from 'react';
import { apiJson } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { FaqBlockListPreview } from './FaqMediaBlock.jsx';
import { Modal, ModalConfirmFooter } from './Modal.jsx';
import { formatDateTime } from '../utils/formatDate.js';
import { SearchableSelect } from './SearchableSelect.jsx';

const BLOCK_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'link', label: 'Link' },
  { value: 'image', label: 'Image', feature: 'faq_media' },
  { value: 'file', label: 'File', feature: 'faq_media' },
  { value: 'credential', label: 'Credentials', feature: 'faq_credentials' },
  { value: 'form', label: 'Intake form', feature: 'faq_forms' },
];

const EMPTY_FORM_FIELD = { key: 'message', label: 'Your message', type: 'textarea', required: true };

/**
 * @param {{ clientId: string | number; projectId?: string | number | null; heading?: string; description?: string }} props
 */
export function AdminClientFaqSection({
  clientId,
  projectId = null,
  heading = 'Client FAQ',
  description = 'Resources and answers for the whole client account (all projects).',
}) {
  const apiBase =
    projectId != null ? `/api/admin/projects/${projectId}/faq` : `/api/admin/clients/${clientId}/faq`;
  const mediaOpts = projectId != null ? { projectId: Number(projectId) } : { clientId: Number(clientId) };
  const { user } = useAuth();
  const caps = user?.capabilities ?? {};
  const [data, setData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** @type {[any, Function]} */
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      apiJson(apiBase),
      caps.faq_forms ? apiJson(`${apiBase}/form-submissions`).catch(() => []) : [],
    ])
      .then(([faq, subs]) => {
        setData(faq);
        setSubmissions(subs);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [apiBase, caps.faq_forms]);

  useEffect(() => {
    if (caps.manage_client_faq) {
      void load();
    } else {
      setLoading(false);
    }
  }, [load, caps.manage_client_faq]);

  const closeModal = () => setModal(null);

  const runModalAction = async (action) => {
    setBusy(true);
    setError('');
    try {
      await action();
      closeModal();
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!caps.manage_client_faq) {
    return (
      <p className="muted small">
        Client FAQ is not included in your subscription. Upgrade your plan to enable the FAQ hub.
      </p>
    );
  }

  if (loading) {
    return <p className="muted">Loading FAQ…</p>;
  }

  const allowed = new Set(data?.plan?.allowed_block_types ?? []);
  const planFeatures = data?.plan?.features ?? {};

  return (
    <div className="faq-admin">
      {error ? <p className="err">{error}</p> : null}

      <div className="row-actions" style={{ marginBottom: '0.75rem' }}>
        <button
          type="button"
          className="btn-primary"
          disabled={busy}
          onClick={() => setModal({ type: 'category', title: '', sort_order: 0, is_published: true })}
        >
          Add category
        </button>
        <span className="muted small">
          Plan: {planFeatures.faq_credentials ? 'credentials' : ''}{' '}
          {planFeatures.faq_media ? 'media' : ''} {planFeatures.faq_forms ? 'forms' : ''}
        </span>
      </div>

      <p className="muted small" style={{ marginBottom: '1rem' }}>
        {description}
      </p>

      {(data?.categories ?? []).map((cat) => (
        <CategoryEditor
          key={cat.id}
          apiBase={apiBase}
          mediaOpts={mediaOpts}
          category={cat}
          allowed={allowed}
          onChange={load}
          onOpenModal={setModal}
        />
      ))}

      {caps.faq_forms && submissions.length > 0 ? (
        <div className="auth-card flat" style={{ marginTop: '1.5rem' }}>
          <h4>Form submissions</h4>
          <ul className="list-plain">
            {submissions.map((s) => (
              <li key={s.id} style={{ marginBottom: '0.75rem' }}>
                <strong>{s.form_title}</strong>
                {s.item_title ? ` · ${s.item_title}` : ''}
                <span className="muted small">
                  {' '}
                  · {s.user?.name || 'Client'} · {formatDateTime(s.created_at)}
                  {s.read_at ? ' (read)' : ''}
                </span>
                <pre className="sync-pre small">{JSON.stringify(s.payload, null, 2)}</pre>
                {!s.read_at ? (
                  <button
                    type="button"
                    className="btn-link"
                    onClick={() =>
                      void apiJson(`${apiBase}/form-submissions/${s.id}/read`, {
                        method: 'PUT',
                      }).then(load)
                    }
                  >
                    Mark read
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <FaqAdminModal
        modal={modal}
        busy={busy}
        apiBase={apiBase}
        allowed={allowed}
        onClose={closeModal}
        onSubmit={runModalAction}
        setError={setError}
      />
    </div>
  );
}

function CategoryEditor({ apiBase, mediaOpts, category, allowed, onChange, onOpenModal }) {
  return (
    <div className="faq-admin-category card-panel" style={{ marginBottom: '1rem' }}>
      <div className="row-actions" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>{category.title}</h3>
        <div className="row-actions">
          <label className="muted small">
            <input
              type="checkbox"
              checked={category.is_published}
              onChange={(ev) =>
                void apiJson(`${apiBase}/categories/${category.id}`, {
                  method: 'PUT',
                  body: { is_published: ev.target.checked },
                }).then(onChange)
              }
            />{' '}
            Published
          </label>
          <button
            type="button"
            className="btn-link"
            onClick={() =>
              onOpenModal({
                type: 'item',
                categoryId: category.id,
                categoryTitle: category.title,
                title: '',
                summary: '',
                is_published: true,
              })
            }
          >
            Add entry
          </button>
          <button
            type="button"
            className="btn-link"
            onClick={() =>
              onOpenModal({
                type: 'confirm',
                action: 'deleteCategory',
                categoryId: category.id,
                title: 'Delete category?',
                message: `Delete "${category.title}" and all entries inside it? This cannot be undone.`,
              })
            }
          >
            Delete
          </button>
        </div>
      </div>

      {(category.items ?? []).map((item) => (
        <ItemEditor
          key={item.id}
          apiBase={apiBase}
          mediaOpts={mediaOpts}
          item={item}
          allowed={allowed}
          onChange={onChange}
          onOpenModal={onOpenModal}
        />
      ))}
    </div>
  );
}

function ItemEditor({ apiBase, mediaOpts, item, allowed, onChange, onOpenModal }) {
  const [newBlockType, setNewBlockType] = useState('text');

  return (
    <div
      className="faq-admin-item"
      style={{ marginTop: '1rem', paddingLeft: '0.5rem', borderLeft: '3px solid var(--border, #e2e8f0)' }}
    >
      <div className="row-actions" style={{ justifyContent: 'space-between' }}>
        <strong>{item.title}</strong>
        <label className="muted small">
          <input
            type="checkbox"
            checked={item.is_published}
            onChange={(ev) =>
              void apiJson(`${apiBase}/items/${item.id}`, {
                method: 'PUT',
                body: { is_published: ev.target.checked },
              }).then(onChange)
            }
          />{' '}
          Published
        </label>
      </div>
      {item.summary ? <p className="muted small">{item.summary}</p> : null}
      <ul className="list-plain faq-block-list">
        {(item.blocks ?? []).map((b) => (
          <li key={b.id} className="faq-block-list-item">
            <div className="faq-block-list-main">
              <span className="faq-block-type-badge">{b.type}</span>
              {b.type === 'text' ? (
                <span className="muted small">{(b.content?.body || '').slice(0, 80)}…</span>
              ) : null}
              {b.type === 'credential' ? (
                <span className="muted small">{b.credential?.label || 'credentials'}</span>
              ) : null}
              {b.type === 'link' ? (
                <span className="muted small">{b.content?.label || b.content?.url}</span>
              ) : null}
              {b.type === 'form' ? <span className="muted small">{b.content?.title || 'Form'}</span> : null}
              {b.type === 'image' || b.type === 'file' ? (
                <>
                  <FaqBlockListPreview block={b} {...mediaOpts} />
                  {b.approval_status && b.approval_status !== 'approved' ? (
                    <span className={`badge badge-${b.approval_status === 'pending' ? 'warn' : 'err'}`}>
                      {b.approval_status}
                    </span>
                  ) : null}
                </>
              ) : null}
            </div>
            <button
              type="button"
              className="btn-link"
              onClick={() =>
                onOpenModal({
                  type: 'confirm',
                  action: 'deleteBlock',
                  blockId: b.id,
                  itemTitle: item.title,
                  title: 'Remove block?',
                  message: `Remove this ${b.type} block from "${item.title}"?`,
                })
              }
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <SearchableSelect value={newBlockType} onChange={(e) => setNewBlockType(e.target.value)}>
          {BLOCK_TYPES.filter((t) => !t.feature || allowed.has(t.value)).map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </SearchableSelect>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => onOpenModal(openBlockModal(newBlockType, item))}
        >
          Add block
        </button>
        <button
          type="button"
          className="btn-link"
          onClick={() =>
            onOpenModal({
              type: 'confirm',
              action: 'deleteItem',
              itemId: item.id,
              title: 'Delete entry?',
              message: `Delete "${item.title}" and all its content blocks?`,
            })
          }
        >
          Delete entry
        </button>
      </div>
    </div>
  );
}

function openBlockModal(blockType, item) {
  const base = { itemId: item.id, itemTitle: item.title, blockType };
  switch (blockType) {
    case 'text':
      return { type: 'blockText', ...base, body: '' };
    case 'link':
      return { type: 'blockLink', ...base, url: '', label: '' };
    case 'credential':
      return {
        type: 'blockCredential',
        ...base,
        credential_label: 'Login',
        credential_username: '',
        credential_password: '',
      };
    case 'form':
      return {
        type: 'blockForm',
        ...base,
        form_title: 'Send information',
        form_description: '',
        fields: [{ ...EMPTY_FORM_FIELD }],
      };
    case 'image':
      return { type: 'blockFile', ...base, accept: 'image/*', fileLabel: 'Image file' };
    case 'file':
      return { type: 'blockFile', ...base, accept: '*/*', fileLabel: 'File' };
    default:
      return { type: 'blockText', ...base, body: '' };
  }
}

/**
 * @param {{ modal: any; busy: boolean; apiBase: string; allowed: Set<string>; onClose: () => void; onSubmit: (fn: () => Promise<void>) => void; setError: (msg: string) => void }} props
 */
function FaqAdminModal({ modal, busy, apiBase, onClose, onSubmit, setError }) {
  if (!modal) {
    return null;
  }

  if (modal.type === 'confirm') {
    return (
      <Modal
        open
        title={modal.title}
        description={modal.message}
        onClose={onClose}
        footer={
          <ModalConfirmFooter
            danger
            confirmLabel="Delete"
            confirmBusy={busy}
            onCancel={onClose}
            onConfirm={() =>
              onSubmit(async () => {
                if (modal.action === 'deleteCategory') {
                  await apiJson(`${apiBase}/categories/${modal.categoryId}`, {
                    method: 'DELETE',
                  });
                } else if (modal.action === 'deleteItem') {
                  await apiJson(`${apiBase}/items/${modal.itemId}`, { method: 'DELETE' });
                } else if (modal.action === 'deleteBlock') {
                  await apiJson(`${apiBase}/blocks/${modal.blockId}`, { method: 'DELETE' });
                }
              })
            }
          />
        }
      >
        <p className="muted small" style={{ margin: 0 }}>
          This action is permanent.
        </p>
      </Modal>
    );
  }

  if (modal.type === 'category') {
    return (
      <CategoryModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} />
    );
  }

  if (modal.type === 'item') {
    return <ItemModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} />;
  }

  if (modal.type === 'blockText') {
    return <BlockTextModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} />;
  }

  if (modal.type === 'blockLink') {
    return <BlockLinkModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} />;
  }

  if (modal.type === 'blockCredential') {
    return (
      <BlockCredentialModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} />
    );
  }

  if (modal.type === 'blockForm') {
    return <BlockFormModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} setError={setError} />;
  }

  if (modal.type === 'blockFile') {
    return <BlockFileModal modal={modal} busy={busy} onClose={onClose} onSubmit={onSubmit} apiBase={apiBase} setError={setError} />;
  }

  return null;
}

function CategoryModal({ modal, busy, onClose, onSubmit, apiBase }) {
  const [form, setForm] = useState(modal);

  return (
    <Modal
      open
      title="New FAQ category"
      description="Group related questions for your client (e.g. Access, Billing, How-to)."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !form.title.trim()}
            onClick={() =>
              onSubmit(() =>
                apiJson(`${apiBase}/categories`, {
                  method: 'POST',
                  body: {
                    title: form.title.trim(),
                    sort_order: Number(form.sort_order) || 0,
                    is_published: form.is_published,
                  },
                }),
              )
            }
          >
            {busy ? 'Saving…' : 'Create category'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 0 }}>
        <label className="field">
          <span>Title *</span>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Website access"
            autoFocus
          />
        </label>
        <label className="field">
          <span>Sort order</span>
          <input
            type="number"
            min={0}
            value={form.sort_order}
            onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
          />
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          />
          <span>Published (visible to client)</span>
        </label>
      </div>
    </Modal>
  );
}

function ItemModal({ modal, busy, onClose, onSubmit, apiBase }) {
  const [form, setForm] = useState(modal);

  return (
    <Modal
      open
      title="New FAQ entry"
      description={`Add a question or topic under "${modal.categoryTitle}".`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !form.title.trim()}
            onClick={() =>
              onSubmit(() =>
                apiJson(`${apiBase}/categories/${modal.categoryId}/items`, {
                  method: 'POST',
                  body: {
                    title: form.title.trim(),
                    summary: form.summary.trim() || null,
                    is_published: form.is_published,
                  },
                }),
              )
            }
          >
            {busy ? 'Saving…' : 'Create entry'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 0 }}>
        <label className="field">
          <span>Title *</span>
          <input
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. WordPress admin login"
            autoFocus
          />
        </label>
        <label className="field">
          <span>Short summary (optional)</span>
          <input
            value={form.summary}
            onChange={(e) => setForm((f) => ({ ...f, summary: e.target.value }))}
            placeholder="Shown collapsed in the client portal"
          />
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={form.is_published}
            onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          />
          <span>Published</span>
        </label>
      </div>
    </Modal>
  );
}

function BlockTextModal({ modal, busy, onClose, onSubmit, apiBase }) {
  const [body, setBody] = useState(modal.body || '');

  return (
    <Modal
      open
      title="Add text block"
      description={`Entry: ${modal.itemTitle}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !body.trim()}
            onClick={() => {
              const fd = new FormData();
              fd.append('type', 'text');
              fd.append('content[body]', body.trim());
              onSubmit(() =>
                apiJson(`${apiBase}/items/${modal.itemId}/blocks`, { method: 'POST', body: fd }),
              );
            }}
          >
            {busy ? 'Saving…' : 'Add text'}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Content *</span>
        <textarea rows={8} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Instructions or answer text…" />
      </label>
    </Modal>
  );
}

function BlockLinkModal({ modal, busy, onClose, onSubmit, apiBase }) {
  const [url, setUrl] = useState(modal.url || '');
  const [label, setLabel] = useState(modal.label || '');

  return (
    <Modal
      open
      title="Add link"
      description={`Entry: ${modal.itemTitle}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !url.trim()}
            onClick={() => {
              const fd = new FormData();
              fd.append('type', 'link');
              fd.append('content[url]', url.trim());
              fd.append('content[label]', (label.trim() || url.trim()));
              onSubmit(() =>
                apiJson(`${apiBase}/items/${modal.itemId}/blocks`, { method: 'POST', body: fd }),
              );
            }}
          >
            {busy ? 'Saving…' : 'Add link'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 0 }}>
        <label className="field">
          <span>URL *</span>
          <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" autoFocus />
        </label>
        <label className="field">
          <span>Link label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Display text (defaults to URL)" />
        </label>
      </div>
    </Modal>
  );
}

function BlockCredentialModal({ modal, busy, onClose, onSubmit, apiBase }) {
  const [form, setForm] = useState(modal);

  return (
    <Modal
      open
      title="Add credentials"
      description={`Stored encrypted. The client sees dots until they click Show. Entry: ${modal.itemTitle}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => {
              const fd = new FormData();
              fd.append('type', 'credential');
              fd.append('credential_label', form.credential_label.trim() || 'Login');
              fd.append('credential_username', form.credential_username);
              fd.append('credential_password', form.credential_password);
              onSubmit(() =>
                apiJson(`${apiBase}/items/${modal.itemId}/blocks`, { method: 'POST', body: fd }),
              );
            }}
          >
            {busy ? 'Saving…' : 'Save credentials'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 0 }}>
        <label className="field">
          <span>Label</span>
          <input
            value={form.credential_label}
            onChange={(e) => setForm((f) => ({ ...f, credential_label: e.target.value }))}
            placeholder="e.g. cPanel, Email, FTP"
          />
        </label>
        <label className="field">
          <span>Username</span>
          <input
            value={form.credential_username}
            onChange={(e) => setForm((f) => ({ ...f, credential_username: e.target.value }))}
            autoComplete="off"
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            value={form.credential_password}
            onChange={(e) => setForm((f) => ({ ...f, credential_password: e.target.value }))}
            autoComplete="new-password"
          />
        </label>
        <p className="muted small" style={{ margin: 0 }}>
          Tip: leave username or password empty if you only need one field.
        </p>
      </div>
    </Modal>
  );
}

function BlockFormModal({ modal, busy, onClose, onSubmit, apiBase, setError }) {
  const [form, setForm] = useState({ ...modal, create_task: false });

  const updateField = (index, patch) => {
    setForm((f) => ({
      ...f,
      fields: f.fields.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const addField = () => {
    setForm((f) => ({
      ...f,
      fields: [...f.fields, { key: `field_${f.fields.length + 1}`, label: 'Field', type: 'text', required: false, options: '' }],
    }));
  };

  const removeField = (index) => {
    setForm((f) => ({ ...f, fields: f.fields.filter((_, i) => i !== index) }));
  };

  const submit = () => {
    const keys = form.fields.map((f) => f.key.trim()).filter(Boolean);
    if (new Set(keys).size !== keys.length) {
      setError('Each form field must have a unique key.');
      return;
    }
    if (keys.length === 0) {
      setError('Add at least one form field.');
      return;
    }

    const fd = new FormData();
    fd.append('type', 'form');
    fd.append('content[title]', form.form_title.trim() || 'Form');
    if (form.form_description.trim()) {
      fd.append('content[description]', form.form_description.trim());
    }
    if (form.create_task) {
      fd.append('content[create_task]', '1');
    }
    form.fields.forEach((f, i) => {
      if (!f.key.trim()) return;
      fd.append(`content[fields][${i}][key]`, f.key.trim());
      fd.append(`content[fields][${i}][label]`, f.label.trim() || f.key.trim());
      fd.append(`content[fields][${i}][type]`, f.type || 'text');
      if (f.required) fd.append(`content[fields][${i}][required]`, '1');
      if (f.type === 'select' && f.options?.trim()) {
        fd.append(`content[fields][${i}][options]`, f.options.trim());
      }
    });

    onSubmit(() =>
      apiJson(`${apiBase}/items/${modal.itemId}/blocks`, { method: 'POST', body: fd }),
    );
  };

  return (
    <Modal
      open
      title="Add intake form"
      description={`Client can submit answers to your agency. Entry: ${modal.itemTitle}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={busy} onClick={submit}>
            {busy ? 'Saving…' : 'Add form'}
          </button>
        </>
      }
    >
      <div className="stack" style={{ marginTop: 0 }}>
        <label className="field">
          <span>Form title</span>
          <input value={form.form_title} onChange={(e) => setForm((f) => ({ ...f, form_title: e.target.value }))} />
        </label>
        <label className="field">
          <span>Description (optional)</span>
          <textarea
            rows={2}
            value={form.form_description}
            onChange={(e) => setForm((f) => ({ ...f, form_description: e.target.value }))}
            placeholder="Instructions for the client"
          />
        </label>
        <label className="field row-actions" style={{ alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={form.create_task}
            onChange={(e) => setForm((f) => ({ ...f, create_task: e.target.checked }))}
          />
          <span>Create a task for staff when submitted</span>
        </label>
        <div>
          <div className="row-actions" style={{ justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <strong className="small">Fields</strong>
            <button type="button" className="btn-link" onClick={addField}>
              + Add field
            </button>
          </div>
          {form.fields.map((f, i) => (
            <div key={i} className="auth-card flat" style={{ marginBottom: '0.5rem', padding: '0.75rem' }}>
              <div className="faq-form-field-row">
                <label className="field">
                  <span className="small">Key</span>
                  <input value={f.key} onChange={(e) => updateField(i, { key: e.target.value })} placeholder="message" />
                </label>
                <label className="field">
                  <span className="small">Label</span>
                  <input value={f.label} onChange={(e) => updateField(i, { label: e.target.value })} />
                </label>
                <label className="field">
                  <span className="small">Type</span>
                  <SearchableSelect value={f.type} onChange={(e) => updateField(i, { type: e.target.value })}>
                    <option value="text">Text</option>
                    <option value="email">Email</option>
                    <option value="textarea">Long text</option>
                    <option value="number">Number</option>
                    <option value="url">URL</option>
                    <option value="tel">Phone</option>
                    <option value="select">Dropdown</option>
                    <option value="checkbox">Checkbox</option>
                  </SearchableSelect>
                </label>
                {f.type === 'select' ? (
                  <label className="field">
                    <span className="small">Options (comma-separated)</span>
                    <input
                      value={f.options || ''}
                      onChange={(e) => updateField(i, { options: e.target.value })}
                      placeholder="Option A, Option B"
                    />
                  </label>
                ) : null}
                <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.35rem' }}>
                  <input
                    type="checkbox"
                    checked={f.required}
                    onChange={(e) => updateField(i, { required: e.target.checked })}
                  />
                  <span className="small">Required</span>
                </label>
              </div>
              {form.fields.length > 1 ? (
                <button type="button" className="btn-link small" onClick={() => removeField(i)}>
                  Remove field
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function BlockFileModal({ modal, busy, onClose, onSubmit, apiBase, setError }) {
  const [file, setFile] = useState(null);
  const [needsApproval, setNeedsApproval] = useState(false);

  return (
    <Modal
      open
      title={modal.blockType === 'image' ? 'Add image' : 'Add file'}
      description={`Upload for "${modal.itemTitle}". Max 20 MB.`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !file}
            onClick={() => {
              const upload = new FormData();
              upload.append('type', modal.blockType);
              upload.append('file', file);
              if (modal.blockType === 'file') {
                upload.append('content[label]', file.name);
              }
              if (needsApproval) {
                upload.append('needs_approval', '1');
              }
              onSubmit(() =>
                apiJson(`${apiBase}/items/${modal.itemId}/blocks`, {
                  method: 'POST',
                  body: upload,
                }),
              );
            }}
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </>
      }
    >
      <label className="field">
        <span>{modal.fileLabel} *</span>
        <input
          type="file"
          accept={modal.accept}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f && f.size > 20 * 1024 * 1024) {
              setError('File must be 20 MB or smaller.');
              e.target.value = '';
              setFile(null);
              return;
            }
            setFile(f || null);
          }}
        />
      </label>
      <label className="field row-actions" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="checkbox"
          checked={needsApproval}
          onChange={(e) => setNeedsApproval(e.target.checked)}
        />
        <span>Require approval before client can download</span>
      </label>
      {file ? (
        <p className="muted small" style={{ margin: 0 }}>
          Selected: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
        </p>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          Choose a file from your computer.
        </p>
      )}
    </Modal>
  );
}
